<template>
  <div class="language-selector flex min-w-0 items-stretch gap-x-1.5">
    <VcDropdownMenu
      placement="top-start"
      width="100%"
      max-height="h-auto"
      :y-offset="-1"
      class="relative w-full min-w-0"
      :class="{ 'open-content': open }"
      @toggle="toggle($event)"
    >
      <template #trigger>
        <div
          class="flex h-10 w-full min-w-0 flex-wrap content-between items-center rounded-md border border-[color:var(--color-neutral-a3)] bg-white py-[10px] pl-[15px] pr-[10px] text-sm leading-[1.3] text-[color:var(--color-footer-top-text)]"
          :class="{ 'rounded-t-none': open }"
        >
          <span class="flex min-w-0 flex-1 items-center gap-2">
            <span class="flex shrink-0 items-center rounded-full text-[color:var(--color-neutral-a2)]">
              <VcIcon class="shrink-0 text-[--color-footer-top-text]" :size="12" name="world" />
            </span>
            <span
              class="min-w-0 flex-1 truncate text-left font-bold"
              :title="$t(`shared.layout.footer.${currentLanguage.twoLetterLanguageName}`)"
            >
              {{ $t(`shared.layout.footer.${currentLanguage.twoLetterLanguageName}`) }}
              <!-- {{ currentLanguage.nativeName }} -->
            </span>
            <VcIcon
              class="shrink-0 stroke-[white] stroke-2 text-[--color-footer-top-text]"
              size="xs"
              :name="open ? 'chevron-up' : 'chevron-down'"
            />
          </span>
        </div>
      </template>

      <template #content>
        <VcMenuItem
          v-for="item in languages"
          :key="item.twoLetterLanguageName"
          :active="item.twoLetterLanguageName === currentLanguage?.twoLetterLanguageName"
          color="neutral"
          @click="
            item.twoLetterLanguageName === currentLanguage?.twoLetterLanguageName
              ? null
              : select(item.twoLetterLanguageName)
          "
        >
          <span class=""> {{ $t(`shared.layout.footer.${item.twoLetterLanguageName}`) }}</span>
        </VcMenuItem>
      </template>
    </VcDropdownMenu>
  </div>
</template>

<script setup lang="ts">
import { ref, shallowRef, computed } from "vue";
import { updateLanguageAndCurrency } from "@/core/api/graphql/account/mutations/updateLanguageAndCurrency";
import { useLanguages } from "@/core/composables";
import { useOverlayLocales } from "@/core/composables/useOverlayLocales";
import { languagesAvailableExclusivelyInCart } from "@/core/constants/language-and-currency";
import { useUser } from "@/shared/account/composables/useUser";
import type { InputUpdateLanguageAndCurrencyType } from "@/core/api/graphql/types";
import type { ILanguage } from "@/core/types/language";

const props = defineProps<IProps>();
interface IProps {
  /** Two-letter codes of the overlay locales (e.g. "de", "fr") the current page has a variant for. */
  pageOverlayLocales?: string[];
}

const { currentLanguage: currentSelectedLanguage, supportedLanguages, saveLocale } = useLanguages();
const { user } = useUser();
const open = ref(false);
const listElement = shallowRef<HTMLElement | null>(null);

// Overlay locales (German, French, …) come from a single registry, so the selector never re-derives
// the per-locale rules. Each engine owns "is it active?" and "should it be listed?"; we only feed it
// whether the current page has that locale's variant.
const overlayLocales = useOverlayLocales();
const pageHasOverlay = (locale: string) => () => props.pageOverlayLocales?.includes(locale) ?? false;
const overlayStates = overlayLocales.map((engine) => ({
  engine,
  isActive: engine.isActiveLanguage(pageHasOverlay(engine.locale)),
  isListed: engine.showOption(pageHasOverlay(engine.locale)),
}));

const currentLanguage = computed(() => {
  // Overlay locales are excluded from supportedLanguages, so currentSelectedLanguage falls back to
  // English on a /{locale}/ URL; the registry tells us when an overlay should be shown as the active
  // language. At most one overlay is active at a time.
  const active = overlayStates.find((state) => state.isActive.value && state.engine.overlayLanguage.value);
  return active?.engine.overlayLanguage.value ?? currentSelectedLanguage.value;
});

const languages = computed<ILanguage[]>(() => {
  // Prepend every listed overlay locale (registry order: German, then French) ahead of the theme
  // languages.
  const overlays = overlayStates
    .filter((state) => state.isListed.value && state.engine.overlayLanguage.value)
    .map((state) => state.engine.overlayLanguage.value as ILanguage);
  return overlays.length ? [...overlays, ...supportedLanguages.value] : supportedLanguages.value;
});

function hideList() {
  const HIDE_TIMEOUT = 350;
  open.value = false;

  setTimeout(() => {
    if (listElement.value) {
      listElement.value.scrollTop = 0;
    }
  }, HIDE_TIMEOUT);
}

function toggle(value: boolean) {
  open.value = value;
}

async function select(locale: string) {
  if (user.value?.contact?.id && !languagesAvailableExclusivelyInCart.find((lang) => lang === locale)) {
    const languageAndCurrencyInput: InputUpdateLanguageAndCurrencyType = {
      userId: user.value?.id,
      language: supportedLanguages.value.find((lang) => lang.twoLetterLanguageName === locale)?.cultureName,
      currency: user.value?.contact.currencyCode,
    };

    await updateLanguageAndCurrency(languageAndCurrencyInput);
  }

  // Record the manual switch for every overlay the current page offers (suppresses its auto-select
  // for the next 30 days).
  for (const { engine } of overlayStates) {
    if (props.pageOverlayLocales?.includes(engine.locale)) {
      engine.saveLanguageChange(locale);
    }
  }
  saveLocale(locale);
  hideList();
}
</script>

<style lang="scss">
// Let the popover/dropdown trigger wrappers shrink so long language names truncate instead of
// pushing the chevron outside the trigger.
.language-selector .vc-popover__trigger,
.language-selector .vc-dropdown-menu__trigger {
  @apply min-w-0;
}

.language-selector .vc-menu-item {
  &__inner {
    @apply p-[10px];

    &.vc-menu-item__inner--size--md {
      @apply py-[9px] px-[15px];
    }
  }
}
.open-content {
  .vc-dropdown-menu__list {
    border-bottom-left-radius: 0 !important;
    border-bottom-right-radius: 0 !important;
  }
}
.language-selector [id^="popover-"],
.language-selector [id*=" popover-"] {
  inset: auto 0px 0px auto !important;
  transform: translate3d(-0.5px, -39px, 0px) !important;
}
</style>
